import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, ExternalLink, Phone, Globe, MapPin, Navigation } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import SubscribeCard from "@/components/SubscribeCard";
import TrackerCard from "@/components/TrackerCard";
import { STATUS_META, formatDate } from "@/lib/status";
import { useI18n } from "@/context/I18nContext";
import { generateVisitorPacket } from "@/lib/pdf";
import { FileDown } from "lucide-react";

export default function FacilityPage() {
  const { id } = useParams();
  const { t } = useI18n();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient.get(`/facilities/${id}`)
      .then(r => setF(r.data))
      .catch(() => setF(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8"><Skeleton className="h-96 w-full" /></div>;
  if (!f) return <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8"><p>Facility not found. <Link to="/" className="text-blue-700 underline">Search again</Link></p></div>;

  const meta = STATUS_META[f.status] || STATUS_META.UNKNOWN;
  const isCritical = ["LOCKDOWN", "VISITATION_SUSPENDED", "EMERGENCY"].includes(f.status);
  const mapUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${f.address}, ${f.city}, ${f.state} ${f.zip_code}`)}`;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8" data-testid="facility-page">
      <div className="flex items-center justify-between gap-3">
        <Link to="/" className="text-sm text-blue-700 hover:underline">{t("common.backSearch")}</Link>
        <Button
          data-testid="download-packet-btn"
          variant="outline"
          size="sm"
          onClick={() => generateVisitorPacket(f, t)}
          className="border-slate-900 text-slate-900 hover:bg-slate-100"
        >
          <FileDown size={14} className="mr-1" /> {t("facility.downloadPacket")}
        </Button>
      </div>

      {/* Header */}
      <div className="mt-3 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{f.system_type === "federal" ? "Federal Bureau of Prisons" : f.system}</div>
          <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" data-testid="facility-name">{f.name}</h1>
          <div className="mt-1 text-sm text-slate-600">{f.facility_type}</div>
        </div>
        <StatusBadge status={f.status} size="lg" />
      </div>

      {/* Traveler Alert */}
      <Alert
        data-testid="traveler-alert-warning"
        className={`mt-6 border-2 ${isCritical ? "border-red-300 bg-red-50" : "border-amber-200 bg-amber-50"}`}
      >
        <AlertTriangle className={isCritical ? "text-red-700" : "text-amber-700"} />
        <AlertTitle className="font-heading font-bold text-slate-900">{t("facility.beforeYouTravel")}</AlertTitle>
        <AlertDescription className="text-slate-800 leading-relaxed">
          <div className="mt-1">
            <span className="font-semibold">{t("facility.currentStatus")}:</span> {t(`status.${f.status}`)} · <span className="font-semibold">{t("facility.visitationStatus")}:</span> {f.visitation_status || "—"}.
          </div>
          <div className="mt-1">{t("facility.beforeTravelLine1")} <span className="font-semibold">{t("facility.beforeTravelLine2")}</span></div>
          <div className="mt-3 flex flex-wrap gap-2">
            {f.phone && <a href={`tel:${f.phone}`} data-testid="call-facility-btn"><Button size="sm" className="bg-slate-900 text-white hover:bg-slate-800"><Phone size={14} className="mr-1" /> {t("facility.callFacility")} {f.phone}</Button></a>}
            {f.status_source_url && <a href={f.status_source_url} target="_blank" rel="noreferrer" data-testid="view-official-notice-btn"><Button size="sm" variant="outline" className="border-slate-900 text-slate-900 hover:bg-slate-100"><ExternalLink size={14} className="mr-1" /> {t("facility.viewOfficialNotice")}</Button></a>}
            <a href={mapUrl} target="_blank" rel="noreferrer" data-testid="directions-btn"><Button size="sm" variant="outline"><Navigation size={14} className="mr-1" /> {t("facility.directions")}</Button></a>
          </div>
        </AlertDescription>
      </Alert>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Institution info */}
        <Card className="p-5 lg:col-span-2 border-slate-200">
          <h2 className="font-heading text-lg font-bold text-slate-900">{t("facility.institutionInfo")}</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <InfoBlock label={t("results.ciAddress")} mono>
              <div className="text-slate-900">{f.address}</div>
              <div className="text-slate-700">{f.city}, {f.state} {f.zip_code}</div>
            </InfoBlock>
            <InfoBlock label={t("facility.phone")}>
              {f.phone ? <a href={`tel:${f.phone}`} className="text-blue-700 hover:underline">{f.phone}</a> : "—"}
            </InfoBlock>
            <InfoBlock label={t("facility.systemType")}>{f.system_type === "federal" ? "Federal" : f.system_type === "state" ? "State" : "County/Local"}</InfoBlock>
            <InfoBlock label={t("facility.facilityType")}>{f.facility_type}</InfoBlock>
            <InfoBlock label={t("facility.officialWebsite")}>
              {f.website ? <a href={f.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline"><Globe size={12}/> {t("home.open")}</a> : "—"}
            </InfoBlock>
            <InfoBlock label={t("facility.visitationPage")}>
              {f.visitation_url ? <a href={f.visitation_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline"><ExternalLink size={12}/> {t("home.open")}</a> : "—"}
            </InfoBlock>
          </div>
        </Card>

        {/* Status panel */}
        <Card className="p-5 border-slate-200">
          <h2 className="font-heading text-lg font-bold text-slate-900">{t("facility.currentStatus")}</h2>
          <div className="mt-3">
            <StatusBadge status={f.status} size="md" />
          </div>
          <p className="mt-3 text-sm text-slate-700 leading-relaxed">{f.status_note || ""}</p>
          <div className="mt-4 space-y-2 text-xs text-slate-600 font-mono">
            <div><span className="uppercase tracking-widest">{t("facility.lastUpdated")}:</span> <span className="text-slate-900">{formatDate(f.status_updated_at)}</span></div>
            <div><span className="uppercase tracking-widest">{t("facility.statusSource")}:</span> <span className="text-slate-900">{f.status_source || "—"}</span></div>
          </div>
          {f.status_source_url && (
            <a href={f.status_source_url} target="_blank" rel="noreferrer" data-testid="official-source-link" className="mt-3 inline-flex items-center gap-1 text-sm text-blue-700 hover:underline">
              <ExternalLink size={14} /> {t("facility.officialSource")}
            </a>
          )}
        </Card>
      </div>

      {/* Visitation */}
      <Card className="mt-6 p-5 border-slate-200" data-testid="visitation-panel">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-heading text-lg font-bold text-slate-900">{t("facility.visitationHeader")}</h2>
          {isCritical && <span className="text-sm font-bold uppercase text-red-700">🔴 {t(`status.${f.status}`)}</span>}
        </div>
        {isCritical && (
          <div className="mt-2 rounded-md border-2 border-red-200 bg-red-50 p-3 text-sm text-red-900">
            <span className="font-semibold">{t("facility.criticalAnnouncement")}</span>
          </div>
        )}
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <InfoBlock label={t("facility.visitationStatus")}>{f.visitation_status || "—"}</InfoBlock>
          <InfoBlock label={t("facility.visitationDays")}>{f.visiting_days || "—"}</InfoBlock>
          <InfoBlock label={t("facility.visitationHours")}>{f.visiting_hours || "—"}</InfoBlock>
          <InfoBlock label={t("facility.visitationPhone")}>
            {f.visitation_phone ? <a href={`tel:${f.visitation_phone}`} className="text-blue-700 hover:underline">{f.visitation_phone}</a> : "—"}
          </InfoBlock>
          <InfoBlock label={t("facility.idRequirements")}>{f.visitor_id_requirements || t("facility.contactId")}</InfoBlock>
          <InfoBlock label={t("facility.visitationRules")}>{f.visitation_rules || "—"}</InfoBlock>
        </div>
      </Card>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <SubscribeCard facility={f} />
        <TrackerCard facility={f} />
      </div>

      <Card className="mt-6 p-5 border-slate-200">
        <h3 className="font-heading text-base font-bold text-slate-900">{t("facility.verifyDirectly")}</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t("facility.verifyDirectlyBody")}</p>
        {f.official_source_url && (
          <a
            href={f.official_source_url}
            target="_blank"
            rel="noreferrer"
            data-testid="verify-official-record-btn"
            className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline"
          >
            {t("facility.openOfficialSource")}
          </a>
        )}
      </Card>
    </div>
  );
}

function InfoBlock({ label, children, mono }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 text-sm ${mono ? "font-mono" : ""} text-slate-900`}>{children}</div>
    </div>
  );
}
